import type {
  UserPortfolioPoolEntryWire,
  UserPortfolioPositionWire,
  UserPortfolioSnapshotWire,
} from '@morfi/backend-sdk-v2/client';
import {
  buildConsolidatedPnlBreakdown,
  buildPnlBreakdown,
  type ConsolidatedPnlHolding,
  checkedAccountTotalD6,
  checkedAverageEntryPriceD9,
  isI64,
  isU64,
  type PnlBreakdownInput,
  POSITION_PNL_VIEW_MODE,
  positionQuoteContext,
  selectPnlView,
} from '@morfi/market-math';
import { USDC_DUST_THRESHOLD_D6 } from '@/config/solana';
import type {
  UserPortfolioDisplayUnavailableReason,
  UserPortfolioPositionDisplayMark,
  UserPortfolioPositionDisplayRow,
  UserPortfolioSummary,
} from './display-types';

type PositionPnlBreakdownInputResult =
  | { available: true; value: PnlBreakdownInput }
  | { available: false; reason: UserPortfolioDisplayUnavailableReason };

export function userPortfolioPositionDisplayRow(
  position: UserPortfolioPositionWire,
  poolEntry: UserPortfolioPoolEntryWire | undefined,
): UserPortfolioPositionDisplayRow {
  return {
    ...position,
    displayMark: userPortfolioPositionDisplayMark(position, poolEntry),
  };
}

export function userPortfolioPositionDisplayMark(
  position: UserPortfolioPositionWire,
  poolEntry: UserPortfolioPoolEntryWire | undefined,
): UserPortfolioPositionDisplayMark {
  return displayMarkFromInput(position, positionPnlBreakdownInput(position, poolEntry));
}

function displayMarkFromInput(
  position: UserPortfolioPositionWire,
  input: PositionPnlBreakdownInputResult,
): UserPortfolioPositionDisplayMark {
  const balanceD6 = unsignedWire(position.balanceD6);
  const costBasisD6 = unsignedWire(position.remainingCostBasisGrossD6);
  // Fee-free basis — the same basis `selectPnlView` marks the open lot against —
  // so average entry lines up with the P&L break-even the chart draws.
  const average =
    balanceD6 === null || costBasisD6 === null
      ? null
      : checkedAverageEntryPriceD9(costBasisD6, balanceD6);
  const averageEntryPriceD9 = average?.available === true ? average.value : null;

  if (!input.available) {
    return unavailablePositionMark(averageEntryPriceD9, input.reason);
  }
  const breakdown = buildPnlBreakdown(input.value);
  if (!breakdown.available) {
    return unavailablePositionMark(averageEntryPriceD9, breakdown.reason);
  }
  const view = selectPnlView(breakdown.value, POSITION_PNL_VIEW_MODE);
  return {
    currentValueD6: view.currentValueD6,
    unrealisedPnlD6: view.unrealizedD6,
    totalPnlD6: view.totalD6,
    averageEntryPriceD9,
    unavailableReason: null,
  };
}

/**
 * Portfolio total consolidates holdings of the same outcome pool before
 * summing — two wallets holding the same outcome exit through one curve, so
 * the second wallet's tokens sell into the price the first already moved.
 * `buildConsolidatedPnlBreakdown` is the same math the server's
 * `/positions-summary` total uses. Per-row `displayMark` stays a standalone
 * per-position valuation (unaffected by any other row).
 *
 * The header is exact or blank: a single position whose pool cannot be valued
 * blanks `holdingsD6` / `totalD6` rather than publishing an understated exact
 * figure. `unvaluedPositions` says how many rows caused it.
 */
export function userPortfolioSummary(
  snapshot: UserPortfolioSnapshotWire,
  wallets: readonly string[],
  quoteMint: string,
): UserPortfolioSummary {
  const selectedWallets = selectedWalletSet(snapshot, wallets);
  const holdings = snapshot.positions.filter(
    (position) => selectedWallets.has(position.wallet) && hasValuableBalance(position),
  );
  let unvaluedPositions = 0;
  let positionCount = 0;
  let standaloneValueD6 = BigInt(0);
  let unavailableReason: UserPortfolioDisplayUnavailableReason | null = null;
  const consolidatedHoldings: ConsolidatedPnlHolding[] = [];

  for (const position of holdings) {
    const poolEntry = snapshot.poolSnapshotsByMarket[position.market];
    const input = positionPnlBreakdownInput(position, poolEntry);
    const mark = displayMarkFromInput(position, input);
    const holding = input.available
      ? positionConsolidatedHolding(position, poolEntry, input.value)
      : null;
    if (mark.currentValueD6 === null || holding === null || !holding.available) {
      // Without a mark neither the value nor the unrealised P&L of a position
      // is known (its prize included), so the header cannot stay exact.
      unvaluedPositions += 1;
      unavailableReason ??= mark.unavailableReason ?? 'invalid_amount';
      continue;
    }
    const valueWithPrizeD6 = mark.currentValueD6 + holding.value.prizeClaimableD6;
    if (valueWithPrizeD6 >= BigInt(USDC_DUST_THRESHOLD_D6)) positionCount += 1;
    standaloneValueD6 += valueWithPrizeD6;
    consolidatedHoldings.push(holding.value);
  }

  // The summed exit can exceed what a curve holds when positions run ahead of
  // the indexed pool state. Every holding was still valued on its own, so the
  // account falls back to the sum of those standalone marks, as the server does.
  const consolidated = buildConsolidatedPnlBreakdown(consolidatedHoldings);
  const valuedHoldingsD6 = consolidated.available
    ? selectPnlView(consolidated.value, POSITION_PNL_VIEW_MODE).currentValueD6 +
      consolidated.value.prizeClaimableD6
    : standaloneValueD6;
  const cashD6 = indexedQuoteBalanceD6(snapshot, selectedWallets, quoteMint);
  if (cashD6 === null) unavailableReason ??= 'missing_quote_balance';
  const total = checkedAccountTotalD6([cashD6 ?? BigInt(0), valuedHoldingsD6]);
  if (!total.available) unavailableReason ??= total.reason;
  const holdingsD6 = unvaluedPositions === 0 ? valuedHoldingsD6 : null;

  return {
    cashD6,
    holdingsD6,
    totalD6: holdingsD6 !== null && cashD6 !== null && total.available ? total.value : null,
    positionCount: holdingsD6 === null ? null : positionCount,
    unvaluedPositions,
    unavailableReason,
  };
}

function positionPnlBreakdownInput(
  position: UserPortfolioPositionWire,
  poolEntry: UserPortfolioPoolEntryWire | undefined,
): PositionPnlBreakdownInputResult {
  const balanceD6 = unsignedWire(position.balanceD6);
  // Signed: an out-of-order replay can leave claim rights below zero, and the
  // position must report that deficit rather than read as a malformed amount.
  const claimRightsBalanceD6 = signedWire(position.claimRightsBalanceD6);
  const remainingCostBasisGrossD6 = unsignedWire(position.remainingCostBasisGrossD6);
  const remainingCostBasisNetD6 = unsignedWire(position.remainingCostBasisNetD6);
  const realizedPnlGrossD6 = signedWire(position.realizedPnlGrossD6);
  const realizedPnlNetD6 = signedWire(position.realizedPnlNetD6);
  const realizedPenaltyD6 = unsignedWire(position.realizedPenaltyD6);
  const prizeClaimableD6 = unsignedWire(position.prizeClaimableD6);
  if (
    balanceD6 === null ||
    claimRightsBalanceD6 === null ||
    remainingCostBasisGrossD6 === null ||
    remainingCostBasisNetD6 === null ||
    realizedPnlGrossD6 === null ||
    realizedPnlNetD6 === null ||
    realizedPenaltyD6 === null ||
    prizeClaimableD6 === null
  ) {
    return { available: false, reason: 'invalid_amount' };
  }
  if (poolEntry === undefined) {
    return { available: false, reason: 'pool_snapshot_missing' };
  }
  if (poolEntry.status === 'incomplete') {
    return { available: false, reason: 'pool_snapshot_incomplete' };
  }
  const context = positionQuoteContext(
    poolEntry.snapshot,
    position.market,
    position.outcomeIndex,
    position.outcomeMint,
  );
  if (!context.available) return context;
  return {
    available: true,
    value: {
      context: context.value,
      outcomeBalanceD6: balanceD6,
      claimRightsBalanceD6,
      remainingCostBasisGrossD6,
      remainingCostBasisNetD6,
      realizedPnlGrossD6,
      realizedPnlNetD6,
      realizedPenaltyD6,
      prizeClaimableD6,
    },
  };
}

type PositionConsolidatedHoldingResult =
  | { available: true; value: ConsolidatedPnlHolding }
  | { available: false; reason: UserPortfolioDisplayUnavailableReason };

/** The same breakdown input, shaped for pool consolidation instead of a single-position quote. */
function positionConsolidatedHolding(
  position: UserPortfolioPositionWire,
  poolEntry: UserPortfolioPoolEntryWire | undefined,
  input: PnlBreakdownInput,
): PositionConsolidatedHoldingResult {
  if (poolEntry === undefined || poolEntry.status !== 'complete') {
    return { available: false, reason: 'pool_snapshot_missing' };
  }
  const { context, ...amounts } = input;
  return {
    available: true,
    value: {
      snapshot: poolEntry.snapshot,
      market: position.market,
      outcomeIndex: position.outcomeIndex,
      outcomeMint: position.outcomeMint,
      ...amounts,
    },
  };
}

function indexedQuoteBalanceD6(
  snapshot: UserPortfolioSnapshotWire,
  wallets: ReadonlySet<string>,
  quoteMint: string,
): bigint | null {
  const byWallet = new Map<string, bigint>();
  for (const row of snapshot.quoteBalances) {
    if (row.quoteMint !== quoteMint || !wallets.has(row.wallet)) continue;
    const value = unsignedWire(row.balanceD6);
    const previous = byWallet.get(row.wallet);
    if (value === null || (previous !== undefined && previous !== value)) return null;
    byWallet.set(row.wallet, value);
  }
  const values: bigint[] = [];
  for (const wallet of wallets) {
    values.push(byWallet.get(wallet) ?? BigInt(0));
  }
  const total = checkedAccountTotalD6(values);
  return total.available ? total.value : null;
}

function selectedWalletSet(
  snapshot: UserPortfolioSnapshotWire,
  wallets: readonly string[],
): ReadonlySet<string> {
  return new Set(wallets.length === 0 ? snapshot.wallets : wallets);
}

// Mirrors the server summary, which values every row it is given: a settled
// position with no tokens left still holds its claimable prize.
function hasValuableBalance(position: UserPortfolioPositionWire): boolean {
  const balance = unsignedWire(position.balanceD6);
  const prize = unsignedWire(position.prizeClaimableD6);
  return (balance !== null && balance > BigInt(0)) || (prize !== null && prize > BigInt(0));
}

function unavailablePositionMark(
  averageEntryPriceD9: bigint | null,
  unavailableReason: UserPortfolioDisplayUnavailableReason,
): UserPortfolioPositionDisplayMark {
  return {
    currentValueD6: null,
    unrealisedPnlD6: null,
    totalPnlD6: null,
    averageEntryPriceD9,
    unavailableReason,
  };
}

function unsignedWire(value: string): bigint | null {
  if (!/^(?:0|[1-9]\d*)$/.test(value)) return null;
  const parsed = BigInt(value);
  return isU64(parsed) ? parsed : null;
}

function signedWire(value: string): bigint | null {
  if (!/^-?(?:0|[1-9]\d*)$/.test(value)) return null;
  const parsed = BigInt(value);
  return isI64(parsed) ? parsed : null;
}
